import type { ActionToApply } from "../../components/chat/ApplyAllActionsPanel";
import { type ProposalCardContent, proposalCardContentOf, textRedlineOfToolContent } from "../../components/chat/proposal-card/card-content";
import { type ChangeThroughoutCardSelection, changeThroughoutSelectionOfToolContent, heldBackReasonOfSelection, textRedlineOfCardSelection, useChangeThroughoutSelectionsVersion } from "../../components/chat/proposal-card/change-throughout-selections";
import type { LivePageSnapshot } from "../../components/chat/proposal-tracker/page-revisions";
import { documentAlreadyReadsAsProposed, documentLandingOfCard } from "../../components/chat/proposal-card/document-already-reads";
import { commentTargetStateOf, replyTargetStateOf } from "../../components/chat/proposal-card/card-target-state";
import { cardMessageOf } from "../../components/chat/proposal-card/card-details";
import type { CommentCardContent } from "../../components/chat/proposal-card/card-content";
import { commentParagraphReadsGone, commentTargetRecoveries } from "../../components/chat/proposal-card/comment-target-recovery";
import { useWatchedDocumentPage } from "../../context/DocumentPageContext";
import { PROPOSAL_CARD_KIND_LABELS, proposalCardKindOf } from "../../components/chat/proposal-card/card-kinds";
import { type ActionFailure, type ChatMessage, getActionIdentifier, isBatchApplicableActionMessage } from "../../types/chat";
import { CANNOT_LOCATE_TARGET_MESSAGE } from "../../utils/errors";
import { REPORT_POLL_TIMEOUT_MS, pollForReport, regenerateRedline } from "../../utils/api";
import { getOoxmlForBookmarks } from "../../utils/document";
import { type RowStrikingCard, rowRemovedByOf } from "../../utils/redline-row-removed";
import { toastError } from "../../utils/toast";
import { saveActionOutcomeOrToast } from "../../utils/action-outcome-save";
import { buildRegenerationFeedback } from "../../utils/untracked-write-guard";
import { redlineTextProposalSchema } from "@common/utils/redline/wire/redline-payload";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useDrawerExpandedInConversation } from "./use-drawer-expanded-in-conversation";
import type React from "react";
import type { ToolStatusSnapshot } from "@common/components/chat/groupAssistantSteps";
import type { useAnalytics } from "../../context/AnalyticsContext";
import { useCardsNotShown } from "../../context/CardsNotShownContext";
import type { useChat } from "../../context/ChatContext";

export interface UseSuggestedActionsInputs extends Pick<
  ReturnType<typeof useChat>,
  | "updateMessageById"
  | "clearActionRefusal"
  | "clearActionFailure"
  | "isActionApplied"
  | "getActionAppliedAt"
  | "isActionDismissed"
  | "getActionFailure"
  | "isActionRefused"
  | "markActionAsRefused"
  | "markActionAsFailed"
  | "markActionAsApplied"
  | "markActionAsDismissed"
  | "markActionAsUndismissed"
  | "markActionAsSuperseded"
  | "getActionSupersession"
  | "getActionPartSupersession"
  | "isActionRemovedFromConflictGroups"
> {
  authHeader: string;
  selectionOoxmlStripped: string;
  clearProposalEdit: (conversationId: string, actionId: string) => void;
  setShowFeedbackFormForMessageId: React.Dispatch<React.SetStateAction<string | null>>;
  sendError: ReturnType<typeof useAnalytics>["sendError"];
  messages: ChatMessage[];
  toolStatusByCallId: Map<string, ToolStatusSnapshot>;
  proposalEngineEnabled: boolean;
  conversationId: string;
  cardNumbers: Map<string, number>;
}

function heldBackReasonOfCard(proposalEngineEnabled: boolean, cardContent: ProposalCardContent | null, page: LivePageSnapshot, changeThroughoutSelection: ChangeThroughoutCardSelection | null): string | undefined {
  if (!proposalEngineEnabled) return undefined;
  if (cardContent?.kind === "reply") return replyTargetStateOf(cardContent.targetCommentId, page)?.heldBackReason;
  if (cardContent?.kind === "comment") return commentTargetStateOf(cardContent.bookmarks, page, commentTargetRecoveries.recoveryFoundNothing(cardContent.bookmarks))?.heldBackReason;
  return heldBackReasonOfSelection(changeThroughoutSelection) ?? undefined;
}

export function useSuggestedActions({ authHeader, selectionOoxmlStripped, updateMessageById, clearActionRefusal, clearActionFailure, clearProposalEdit, markActionAsSuperseded, setShowFeedbackFormForMessageId, sendError, messages, toolStatusByCallId, isActionApplied, getActionAppliedAt, isActionDismissed, getActionFailure, isActionRefused, markActionAsRefused, markActionAsFailed, markActionAsApplied, markActionAsDismissed, markActionAsUndismissed, proposalEngineEnabled, conversationId, cardNumbers, getActionSupersession, getActionPartSupersession, isActionRemovedFromConflictGroups }: UseSuggestedActionsInputs) {
  const { reasonOf: cardNotShownReasonOf } = useCardsNotShown("suggested_actions");
  const [regeneratingToolResults, setRegeneratingToolResults] = useState<Set<string>>(new Set());
  const [actionsToApply, setActionsToApply] = useState<ActionToApply[]>([]);
  const hasUnappliedUndismissedActions = actionsToApply.some((action) => !action.isApplied && !action.isDismissed);
  const [isQuickActionsPanelExpanded, setIsQuickActionsPanelExpanded] = useDrawerExpandedInConversation(conversationId, hasUnappliedUndismissedActions);
  const regenerateMessageRedline = useCallback(async (message: ChatMessage, feedback: string) => {
    function cardContentWithMarkupDroppedSoTheTextCardWins(toolMessageContent: Record<string, any>) {
      const { ooxml: _replacedMarkup, incoming_section_ooxml: _replacedIncoming, original_section_ooxml: _replacedOriginal, ...contentWithoutMarkup } = toolMessageContent;
      return contentWithoutMarkup;
    }
    function cardContentWithTextCardDroppedSoTheMarkupWins(toolMessageContent: Record<string, any>) {
      const { redline: _replacedTextCard, ...contentWithoutTextCard } = toolMessageContent;
      return contentWithoutTextCard;
    }
    try {
      if (!message.id) return;
      if (!authHeader) return;
      if (message.toolName === "change_throughout") return;
      setRegeneratingToolResults(prev => new Set(prev).add(message.id!));

      const toolMessageContent = JSON.parse(message.content);
      const toolMessageBookmarks = proposalEngineEnabled
        ? toolMessageContent.bookmarks ?? toolMessageContent.after_bookmarks
        : toolMessageContent.bookmarks;

      const currentSectionOoxml = (toolMessageBookmarks && toolMessageBookmarks.length > 0) ?
        await getOoxmlForBookmarks(toolMessageBookmarks) :
        (selectionOoxmlStripped || toolMessageContent.original_section_ooxml);

      const previousTexts = redlineTextProposalSchema.safeParse(toolMessageContent.redline);
      const { report_file } = await regenerateRedline(
        authHeader,
        currentSectionOoxml,
        toolMessageContent.incoming_section_ooxml ?? "",
        toolMessageContent.original_section_ooxml ?? "",
        toolMessageContent.regenerate_context,
        feedback,
        previousTexts.success ? previousTexts.data : null,
      );
      const result = await pollForReport(report_file, REPORT_POLL_TIMEOUT_MS);

      if (result?.status === "DONE" && result?.redline) {
        updateMessageById(message.id, {
          content: JSON.stringify({
            ...cardContentWithMarkupDroppedSoTheTextCardWins(toolMessageContent),
            redline: redlineTextProposalSchema.parse(result.redline),
            bookmarks: result.bookmarks ?? toolMessageBookmarks
          })
        });

        const regeneratedActionId = getActionIdentifier(message, message.id);
        if (proposalEngineEnabled) clearProposalEdit(conversationId, regeneratedActionId);
        clearActionRefusal(conversationId, regeneratedActionId);
        clearActionFailure(conversationId, regeneratedActionId);
        setActionsToApply(prev => prev.map(a =>
          a.id === regeneratedActionId ? { ...a, isRefused: false, failureReason: undefined } : a
        ));

        setShowFeedbackFormForMessageId(prev => (prev === message.id ? null : prev));
      } else if (result?.status === "DONE" && result?.section_ooxml) {
        updateMessageById(message.id, {
          content: JSON.stringify({
            ...cardContentWithTextCardDroppedSoTheMarkupWins(toolMessageContent),
            ooxml: result.section_ooxml
          })
        });

        const regeneratedActionId = getActionIdentifier(message, message.id);
        if (proposalEngineEnabled) clearProposalEdit(conversationId, regeneratedActionId);
        clearActionRefusal(conversationId, regeneratedActionId);
        clearActionFailure(conversationId, regeneratedActionId);
        setActionsToApply(prev => prev.map(a =>
          a.id === regeneratedActionId ? { ...a, isRefused: false, failureReason: undefined } : a
        ));

        setShowFeedbackFormForMessageId(prev => (prev === message.id ? null : prev));
      } else if (result?.status === "ERROR") {
        throw new Error(result.error || "Failed to regenerate section");
      } else {
        throw new Error("Invalid response from regeneration API");
      }
    } catch (err) {
      toastError(err);
      sendError(err instanceof Error ? err : new Error(String(err)), {
        component: 'ChatPage',
        action: "redline_regenerate",
        toolName: message.toolName
      });
    } finally {
      setRegeneratingToolResults(prev => {
        const next = new Set(prev);
        if (message.id) next.delete(message.id);
        return next;
      });
    }
  }, [
    authHeader,
    clearActionFailure,
    clearActionRefusal,
    clearProposalEdit,
    conversationId,
    proposalEngineEnabled,
    selectionOoxmlStripped,
    sendError,
    updateMessageById,
          setShowFeedbackFormForMessageId
    ]);
  const watchedBookmarks = useMemo(() => Array.from(new Set(messages.flatMap(message => {
    if (!isBatchApplicableActionMessage(message)) return [];
    const textRedline = textRedlineOfToolContent(message.content);
    if (textRedline !== null) return textRedline.paragraphs.flatMap(paragraph => (paragraph.bookmark === null ? [] : [paragraph.bookmark]));
    if (!proposalEngineEnabled) return [];
    const content = proposalCardContentOf(message.toolName, message.content, message.bookmarks);
    return content?.kind === "comment" ? content.bookmarks : [];
  }))), [messages, proposalEngineEnabled]);
  const hasReplyCards = useMemo(() => proposalEngineEnabled && messages.some(message => isBatchApplicableActionMessage(message) && message.toolName === "reply_to_comment"), [messages, proposalEngineEnabled]);
  const page = useWatchedDocumentPage(watchedBookmarks, hasReplyCards);
  const commentContents = useMemo<CommentCardContent[]>(() => messages.flatMap(message => {
    if (!isBatchApplicableActionMessage(message) || message.toolName !== "add_comment") return [];
    const content = proposalCardContentOf(message.toolName, message.content, message.bookmarks);
    return content?.kind === "comment" ? [content] : [];
  }), [messages]);
  const commentTargetRecoveriesVersion = useSyncExternalStore(commentTargetRecoveries.subscribe, commentTargetRecoveries.version);
  const changeThroughoutSelectionsVersion = useChangeThroughoutSelectionsVersion();
  useEffect(() => {
    if (!proposalEngineEnabled) return;
    for (const commentContent of commentContents) commentTargetRecoveries.followTarget(commentContent, commentParagraphReadsGone(commentContent.bookmarks, page));
  }, [commentContents, page, proposalEngineEnabled]);
  const derivedActions = useMemo<ActionToApply[]>(() => {
    const actions: ActionToApply[] = [];
    const strikingCards: RowStrikingCard[] = [];
    messages.forEach((message, index) => {
      if (!isBatchApplicableActionMessage(message)) return;
      const strikingId = getActionIdentifier(message, `msg-${index}`);
      const strikingCardNumber = cardNumbers.get(strikingId);
      const strikingRedline = textRedlineOfToolContent(message.content);
      if (strikingCardNumber === undefined || strikingRedline === null || strikingRedline.tableChanges.rowDeletions.length === 0 || !isActionApplied(conversationId, strikingId)) return;
      strikingCards.push({ cardNumber: strikingCardNumber, textRedline: strikingRedline });
    });

    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];
      if (isBatchApplicableActionMessage(message)) {
        const toolName = message.toolName;
        const messageId = getActionIdentifier(message, `msg-${i}`);
        const cardNumber = cardNumbers.get(messageId);
        if (cardNumber === undefined) continue;

        const tcid = (message.turnArtifacts as any)?.tool_call_id;
        if (tcid && toolStatusByCallId.get(tcid)?.status === "error") continue;

        const applied = isActionApplied(conversationId, messageId);
        const dismissed = isActionDismissed(conversationId, messageId);
        const failure = getActionFailure(conversationId, messageId);

        let type: 'redline' | 'comment' | 'paragraph' | 'reply';
        if (toolName === "add_comment") {
          type = "comment";
        } else if (toolName === "reply_to_comment") {
          type = "reply";
        } else if (toolName === "add_paragraph") {
          type = "paragraph";
        } else {
          type = "redline";
        }

        let description = "Edit section";
        try {
          const content = JSON.parse(message.content);
          if (toolName === "add_comment") {
            description = content.text || "Add comment";
            if (description.length > 50) {
              description = description.substring(0, 47) + "...";
            }
          } else if (toolName === "reply_to_comment") {
            description = content.text || "Reply to comment";
            if (description.length > 50) {
              description = description.substring(0, 47) + "...";
            }
          } else if (toolName === "add_paragraph") {
            description = "Add new paragraph";
          } else if (toolName === "edit_table") {
            description = "Edit table";
          } else if (toolName === "change_throughout") {
            description = "Change throughout";
          } else {
            const cardKind = proposalCardKindOf(toolName, textRedlineOfToolContent(message.content));
            if (cardKind === "revert" || cardKind === "revertOwn") description = PROPOSAL_CARD_KIND_LABELS[cardKind];
          }
        } catch {
          description = toolName === "edit_table" ? "Edit table" : "Edit section";
        }
        const alreadyReadsAsProposed = type === "redline" && documentAlreadyReadsAsProposed(textRedlineOfToolContent(message.content), page);
        const rowRemovedBy = type === "redline" ? rowRemovedByOf(textRedlineOfToolContent(message.content), page.removedRowKeyByBookmark, strikingCards) : undefined;
        const cardContent = type === "comment" || type === "reply" ? proposalCardContentOf(toolName, message.content, message.bookmarks) : null;
        const changeThroughoutSelection = changeThroughoutSelectionOfToolContent(messageId, message.content);
        const heldBackReason = heldBackReasonOfCard(proposalEngineEnabled, cardContent, page, changeThroughoutSelection) ?? cardNotShownReasonOf(messageId);
        const supersededBy = getActionSupersession(conversationId, messageId);
        const partSupersededBy = getActionPartSupersession(conversationId, messageId);
        const appliedAtMs = getActionAppliedAt(conversationId, messageId);
        const pageReadAfterTheApply = appliedAtMs === undefined || page.readStartedAtMs > appliedAtMs;
        const documentLanding = proposalEngineEnabled && type === "redline" && applied && pageReadAfterTheApply && supersededBy === undefined && partSupersededBy === undefined
          && documentLandingOfCard(textRedlineOfCardSelection(changeThroughoutSelection, message.content), page);
        const revertedInDocument = documentLanding === "not_applied";
        const partlyRevertedInDocument = documentLanding === "partial" && failure === undefined;
        const standsApplied = applied && !revertedInDocument;

        actions.push({
          id: messageId,
          cardNumber,
          type,
          description,
          isApplied: standsApplied,
          isDismissed: dismissed,
          isRefused: isActionRefused(conversationId, messageId),
          failureReason: failure?.reason,
          isPartiallyApplied: failure?.isPartiallyApplied ?? false,
          writtenBack: failure?.writtenBack === true,
          supersededBy,
          partSupersededBy,
          isRemovedFromConflictGroups: isActionRemovedFromConflictGroups(conversationId, messageId),
          documentAlreadyReadsAsProposed: alreadyReadsAsProposed,
          revertedInDocument,
          partlyRevertedInDocument,
          rowRemovedBy,
          heldBackReason,
          isSelected: !applied && !dismissed && !failure?.isPartiallyApplied && !alreadyReadsAsProposed && heldBackReason === undefined
        });
      }
    }

    return actions;
  }, [messages, cardNumbers, toolStatusByCallId, conversationId, isActionApplied, getActionAppliedAt, isActionDismissed, isActionRefused, getActionFailure, getActionSupersession, getActionPartSupersession, isActionRemovedFromConflictGroups, cardNotShownReasonOf, page, commentTargetRecoveriesVersion, proposalEngineEnabled, changeThroughoutSelectionsVersion]);
  const actionsSignature = JSON.stringify([
    conversationId,
    derivedActions.map(a => [a.id, a.type, a.description, a.isApplied, a.isDismissed, a.isRefused, a.failureReason ?? "", a.isPartiallyApplied, a.isSelected, a.supersededBy ?? "", a.partSupersededBy ?? "", a.isRemovedFromConflictGroups ?? false, a.documentAlreadyReadsAsProposed ?? false, a.revertedInDocument ?? false, a.partlyRevertedInDocument ?? false, a.rowRemovedBy ?? "", a.heldBackReason ?? ""])
  ]);
  const derivedActionsRef = useRef(derivedActions);
  derivedActionsRef.current = derivedActions;
  useEffect(() => {
    setActionsToApply(derivedActionsRef.current);
  }, [actionsSignature]);
  const handleActionRefused = useCallback((actionId: string, lost: string[], added: string[]) => {
    markActionAsRefused(conversationId, actionId);

    setActionsToApply(prev => prev.map(a =>
      a.id === actionId ? { ...a, isRefused: true } : a
    ));

    const message = cardMessageOf(messages, actionId);
    if (!message) return;

    void regenerateMessageRedline(message, buildRegenerationFeedback(lost, added));
  }, [conversationId, markActionAsRefused, messages, regenerateMessageRedline]);
  const tableCardContentOf = useCallback((message: ChatMessage) => {
    const content = proposalCardContentOf(message.toolName, message.content, message.bookmarks);
    return content !== null && (content.kind === "table" || content.kind === "redline") ? content : null;
  }, []);
  const appliedRowDeletionOver = useCallback((actionId: string): string | null => {
    const lostMessage = cardMessageOf(messages, actionId);
    const lostContent = lostMessage === null ? null : tableCardContentOf(lostMessage);
    if (lostContent === null) return null;
    const lostCells = new Set(lostContent.bookmarks);
    for (const message of messages) {
      if (message.id === undefined) continue;
      const otherId = getActionIdentifier(message, message.id);
      if (otherId === actionId || !isActionApplied(conversationId, otherId)) continue;
      const content = tableCardContentOf(message);
      const textRedline = content?.textRedline ?? null;
      if (textRedline === null || textRedline.tableChanges.rowDeletions.length === 0) continue;
      const deletedCells = new Set([...textRedline.paragraphs.flatMap(paragraph => (paragraph.bookmark === null ? [] : [paragraph.bookmark])), ...textRedline.tableChanges.rowDeletions.map(deletion => deletion.rowOfBookmark)]);
      if (Array.from(lostCells).some(cell => deletedCells.has(cell))) {
        const keeper = cardNumbers.get(otherId);
        return keeper === undefined ? null : `#${keeper}`;
      }
    }
    return null;
  }, [messages, tableCardContentOf, isActionApplied, conversationId, cardNumbers]);
  const handleActionFailed = useCallback((actionId: string, failure: ActionFailure) => {
    const supersededBy = failure.reason === CANNOT_LOCATE_TARGET_MESSAGE ? appliedRowDeletionOver(actionId) : null;
    if (supersededBy !== null) {
      markActionAsSuperseded(conversationId, actionId, supersededBy);
      setActionsToApply(prev => prev.map(a => (a.id === actionId ? { ...a, supersededBy, failureReason: undefined } : a)));
      return;
    }
    markActionAsFailed(conversationId, actionId, failure);

    setActionsToApply(prev => prev.map(a =>
      a.id === actionId
        ? { ...a, failureReason: failure.reason, isPartiallyApplied: failure.isPartiallyApplied, writtenBack: failure.writtenBack === true, isSelected: a.isSelected && !failure.isPartiallyApplied }
        : a
    ));
  }, [conversationId, markActionAsFailed, markActionAsSuperseded, appliedRowDeletionOver]);
  const handleActionApplied = useCallback((actionId: string) => {
    markActionAsApplied(conversationId, actionId);

    setActionsToApply(prev => prev.map(a =>
      a.id === actionId ? { ...a, isApplied: true, failureReason: undefined, supersededBy: undefined } : a
    ));
  }, [conversationId, markActionAsApplied]);
  const handleActionDismissed = useCallback((actionId: string) =>
    saveActionOutcomeOrToast(() => markActionAsDismissed(conversationId, actionId)), [conversationId, markActionAsDismissed]);
  const handleActionUndismissed = useCallback((actionId: string) => {
    void saveActionOutcomeOrToast(() => markActionAsUndismissed(conversationId, actionId));
  }, [conversationId, markActionAsUndismissed]);
  const handleToggleSelection = useCallback((actionId: string) => {
    setActionsToApply(prev => prev.map(a =>
      a.id === actionId && !a.isPartiallyApplied && !a.documentAlreadyReadsAsProposed && a.heldBackReason === undefined ? { ...a, isSelected: !a.isSelected } : a
    ));
  }, []);
  const handleToggleSelectAll = useCallback(() => {
    setActionsToApply(prev => {
      const selectable = prev.filter(a => !a.isApplied && !a.isDismissed && !a.isPartiallyApplied && !a.documentAlreadyReadsAsProposed && a.heldBackReason === undefined);
      const allSelected = selectable.length > 0 && selectable.every(a => a.isSelected);
      return prev.map(a =>
        (a.isApplied || a.isDismissed || a.isPartiallyApplied || a.documentAlreadyReadsAsProposed || a.heldBackReason !== undefined) ? a : { ...a, isSelected: !allSelected }
      );
    });
  }, []);
  return { isQuickActionsPanelExpanded, setIsQuickActionsPanelExpanded, hasUnappliedUndismissedActions, actionsToApply, regeneratingToolResults, handleActionRefused, handleActionFailed, handleActionApplied, handleActionDismissed, handleActionUndismissed, handleToggleSelection, handleToggleSelectAll, regenerateMessageRedline, setRegeneratingToolResults, setActionsToApply };
}
